import {useCallback, useEffect, useState} from "react";

export const useAvatarMedia = ({
  avatarSessionState,
  AvatarSessionState,
  avatarState,
  mediaStream,
  remoteAudioElementRef,
  volume,
  logAvatarDebug,
}) => {
  const [needsUserUnmute, setNeedsUserUnmute] = useState(false);

  const safePlayVideo = useCallback(async () => {
    const video = mediaStream.current;
    if (!video) return;

    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.playsInline = true;

    try {
      if (video.paused) {
        await video.play();
      }
      logAvatarDebug("video play success");
      setNeedsUserUnmute(false);
    } catch (error) {
      console.warn("Autoplay with sound failed, retrying muted:", error);

      try {
        video.muted = true;
        if (video.paused) {
          await video.play();
        }
        logAvatarDebug("video muted play success");
        setNeedsUserUnmute(true);
      } catch (mutedError) {
        console.error("Muted autoplay failed:", mutedError);
      }
    }
  }, [logAvatarDebug, mediaStream]);

  const safePlayAudio = useCallback(async () => {
    const audio = remoteAudioElementRef.current;
    if (!audio) return;

    try {
      await audio.play();
      logAvatarDebug("safePlayAudio success");
      setNeedsUserUnmute(false);
    } catch (_error) {
      logAvatarDebug("safePlayAudio blocked");
      setNeedsUserUnmute(true);
    }
  }, [logAvatarDebug, remoteAudioElementRef]);

  useEffect(() => {
    if (avatarState.stream && mediaStream.current) {
      mediaStream.current.srcObject = avatarState.stream;
      mediaStream.current.onloadedmetadata = () => {
        safePlayVideo();
      };
    }
  }, [avatarState.stream, mediaStream, safePlayVideo]);

  useEffect(() => {
    if (mediaStream?.current) {
      mediaStream.current.volume = volume;
    }
    if (remoteAudioElementRef.current) {
      remoteAudioElementRef.current.volume = volume;
    }
  }, [volume, mediaStream, remoteAudioElementRef]);

  useEffect(() => {
    if (!needsUserUnmute || !mediaStream.current) return;

    const unmuteAfterGesture = async () => {
      const video = mediaStream.current;
      if (!video && !remoteAudioElementRef.current) return;

      try {
        if (video) {
          video.muted = false;
          video.volume = volume;
          if (video.paused) {
            await video.play();
          }
        }
        if (remoteAudioElementRef.current) {
          remoteAudioElementRef.current.muted = false;
          remoteAudioElementRef.current.volume = volume;
          await safePlayAudio();
        }
        setNeedsUserUnmute(false);
      } catch (error) {
        console.warn("Unmute after user gesture failed:", error);
      }
    };

    window.addEventListener("touchstart", unmuteAfterGesture, {once: true});
    window.addEventListener("click", unmuteAfterGesture, {once: true});
    window.addEventListener("keydown", unmuteAfterGesture, {once: true});

    return () => {
      window.removeEventListener("touchstart", unmuteAfterGesture);
      window.removeEventListener("click", unmuteAfterGesture);
      window.removeEventListener("keydown", unmuteAfterGesture);
    };
  }, [
    mediaStream,
    needsUserUnmute,
    remoteAudioElementRef,
    safePlayAudio,
    volume,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        avatarSessionState === AvatarSessionState.CONNECTED
      ) {
        safePlayVideo();
        safePlayAudio();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [AvatarSessionState, avatarSessionState, safePlayAudio, safePlayVideo]);

  return {
    safePlayAudio,
  };
};
